/**
 * Character tables for the code pages almost every ESC/POS printer ships with.
 * Each table lists the characters for bytes 0x80–0xFF; 0x20–0x7E are plain ASCII everywhere.
 * `select` is the n in `ESC t n` (Epson numbering, followed by most clones).
 *
 * Anything that is in none of these (₹, Telugu, Devanagari…) is either rendered as a raster
 * image (profile.rasterText) or replaced by a fallback (see sanitizeText).
 */
const CP437 =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';

const CP850 =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ ';

// CP858 = CP850 with the euro sign at 0xD5 (where CP850 has dotless i).
const CP858 = `${CP850.slice(0, 0x55)}€${CP850.slice(0x56)}`;

// Windows-1252: 0x80–0x9F are typographic extras, 0xA0–0xFF match Latin-1 / Unicode.
const WPC1252 =
  '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f\u0090‘’“”•–—˜™š›œ\u009džŸ' +
  Array.from({ length: 96 }, (_, i) => String.fromCharCode(0xa0 + i)).join('');

export const CODE_PAGES = Object.freeze({
  cp437: { label: 'PC437 (USA, standard)', select: 0, table: CP437 },
  cp850: { label: 'PC850 (Multilingual)', select: 2, table: CP850 },
  cp858: { label: 'PC858 (Multilingual + €)', select: 19, table: CP858 },
  wpc1252: { label: 'WPC1252 (Windows Latin)', select: 16, table: WPC1252 },
});

export const CODE_PAGE_KEYS = Object.freeze(Object.keys(CODE_PAGES));

const reverse = new Map();
function reverseOf(codePage) {
  let map = reverse.get(codePage);
  if (!map) {
    const page = CODE_PAGES[codePage] ?? CODE_PAGES.cp437;
    map = new Map([...page.table].map((ch, i) => [ch, 0x80 + i]));
    reverse.set(codePage, map);
  }
  return map;
}

/** True when every character can be printed as text in this code page. */
export function isEncodable(text, codePage = 'cp437') {
  const map = reverseOf(codePage);
  for (const ch of text) {
    const c = ch.codePointAt(0);
    if (c >= 0x20 && c < 0x7f) continue;
    if (c === 0x0a) continue;
    if (!map.has(ch)) return false;
  }
  return true;
}

/** Encode text to code-page bytes. Unknown characters become "?" (call sanitizeText first). */
export function encodeText(text, codePage = 'cp437') {
  const map = reverseOf(codePage);
  const out = [];
  for (const ch of text) {
    const c = ch.codePointAt(0);
    if (c >= 0x20 && c < 0x7f) out.push(c);
    else if (map.has(ch)) out.push(map.get(ch));
    else out.push(0x3f);
  }
  return out;
}

const ASCII_FALLBACKS = new Map(
  Object.entries({
    '‘': "'",
    '’': "'",
    '‚': ',',
    '“': '"',
    '”': '"',
    '„': '"',
    '–': '-',
    '—': '-',
    '−': '-',
    '…': '...',
    '•': '*',
    '·': '.',
    '×': 'x',
    '÷': '/',
    '€': 'EUR',
    ' ': ' ',
    '​': '',
    '‌': '',
    '‍': '',
  }),
);

/**
 * Make text printable in a code page without raster images:
 * ₹ → rupeeFallback ("Rs." by default), smart quotes → ASCII, accents stripped when the
 * accented letter isn't in the page, anything else → "?" (one per run, so a Telugu word
 * becomes a single "?" rather than one per code point).
 */
export function sanitizeText(text, { codePage = 'cp437', rupeeFallback = 'Rs.' } = {}) {
  const map = reverseOf(codePage);
  let out = '';
  for (const ch of String(text ?? '')) {
    const c = ch.codePointAt(0);
    // Combining marks (Indic vowel signs, accents) never stand alone.
    if (/\p{M}/u.test(ch) && out.endsWith('?')) continue;
    if ((c >= 0x20 && c < 0x7f) || map.has(ch)) {
      out += ch;
      continue;
    }
    if (ch === '₹') {
      out += rupeeFallback;
      continue;
    }
    if (c === 0x09) {
      out += ' ';
      continue;
    }
    if (c < 0x20 || c === 0x7f) continue;
    if (ASCII_FALLBACKS.has(ch)) {
      const f = ASCII_FALLBACKS.get(ch);
      out += [...f].every((x) => x.charCodeAt(0) < 0x7f || map.has(x)) ? f : '?';
      continue;
    }
    const base = ch.normalize('NFD').replace(/\p{M}/gu, '');
    if (base && base !== ch && isEncodable(base, codePage)) out += base;
    else if (!out.endsWith('?')) out += '?';
  }
  return out;
}
